/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-31d (22/10/2026) — tab "Đèn NEMA" của trang tủ (và bảng tủ dưới bản đồ), theo thiết kế bản 2 `Main.dc.html`.
 * TK-35 lượt 2 (lee 22/10) — tab GỘP MỌI VIỆC VỀ ĐÈN CỦA TỦ (đèn thuộc tủ; nhóm, lịch dim theo tủ), thứ tự từ trên xuống:
 *   1. Thanh tủ–đèn: "Đèn của tủ: n · đang cấp điện: n · sáng: n" + Nhận đèn (tác vụ ảo) + Xếp vị trí đèn trên bản đồ
 *      (TK-44: → /cai-dat-ban-dau?tu=<id>&xep=1, chỉ ADMIN — xếp đèn là việc của Cài đặt ban đầu) + Quét / Dừng. Ngõ ra đang tắt ⇒ một dòng mảnh "Ngõ ra 2 đang tắt theo lịch — 3 đèn chưa
 *      cấp điện" (trạng thái, xám, KHÔNG đỏ — đèn không có điện không phải lỗi).
 *   2. Danh sách đèn: Đèn · Nhánh (Ngõ 1 / Ngõ 2 / — ; sửa tại chỗ, `<CoQuyen quan_ly_tu>`) · Trạng thái · Dim · Công suất ·
 *      Vị trí (Đã xếp / Chưa xếp) · Cập nhật. Bấm dòng ⇒ khung chi tiết đèn.
 *   3. Nhóm dim (theo tủ): danh sách + tạo/xoá + tích thành viên; Dim: Cả tủ / Nhóm / Đèn + Về lịch.
 *   4. (TK-39) Lịch dim đã chuyển sang tab "Độ sáng đèn" của /cai-dat-chien-luoc (32 timer theo nhóm).
 *   5. Vấn đề (phạm vi tủ) + Lệnh gần đây.
 *
 * 🔴 LUẬT SỐ 0: số NULL vẽ "—" (`so()`), không bao giờ 0; `capDien` null (tủ mất kết nối) ⇒ không đếm, không khai.
 * Badge do máy chủ tính (một hàm với luật). Quy tắc giao diện (lee 22/10): không kênh RF/PAN/MAC/addr hex ở bảng (chỉ khung
 * chi tiết kỹ thuật của tủ); không câu diễn giải. Lệnh chưa có proto ⇒ "Chờ giao thức", không "thất bại".
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { KIEU_GOI_Y, defsMien, toMien, useIdMien } from '../bieudo/Mien';
import { Badge, Button, Spinner, EmptyState, ErrorBox, useToast } from '../ui';
import { HopVanDe } from '../HopVanDe';
import { KhoaTheoTuyen } from '../Khoa';
import { CoQuyen, useAuth } from '../../lib/auth';
import { errorMessage } from '../../lib/api';
import type { QuyetDinhCoTuyen } from '../../lib/quyen';
import { chuaDatTen, chuLyDo, demNhanLenh, khoaLenhDen, type LyDoKhongNhan } from '../../lib/nemaNhan';
import {
  cauLenh, ketQuaLenh, NHAN_BADGE, NHAN_TRANG_THAI_LENH, tenDen, tuoiNgan, useGuiLenhNema, useNema24h, useNemaTu, useNhomTu, useSuaDenNema, useSuaNhomTu,
  type DenNema, type LenhNema, type TuNema,
} from '../../lib/nemaApi';
import { chuaXuongDen, tenNhomSo } from '../../lib/caiDat';
import { api } from '../../lib/api';
import { iMa } from '../../lib/vanHanh';
import { NutNhay } from './NutNhay';
import { CongTacHost } from './CongTacHost';
import { useSchedule } from '../../lib/queries';
import { batKeTiep, nhanGioBat, type NgoLich } from '../../lib/gioCapDien';

/** "—" cho NULL; đơn vị gói card: i_10ma → A (÷100), pf_pct → ÷100. */
const so = (v: number | null | undefined, donVi = '', chia = 1, le = 0) => v == null ? '—' : `${(v / chia).toFixed(le).replace('.', ',')}${donVi ? ` ${donVi}` : ''}`;
const MAU_NHOM = ['#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#ec4899', '#14b8a6'];
type Qd = QuyetDinhCoTuyen | 'khong-co-tuyen' | 'chua-biet';

/** `qd` = quyết định cờ tuyến BA CA (như thẻ ngõ ra): chỉ 'cho-qua' mới có nút lệnh. Nhận `qd`, không nhận boolean. */
export function TabDenNema({ cabinetId, tenTu, qd }: { cabinetId: string; tenTu: string; qd: Qd }) {
  const canControl = qd === 'cho-qua';
  /* TK-44 §4: "Xếp vị trí" mở Cài đặt ban đầu — cùng cổng vai với mục menu ấy (ADMIN trở lên) */
  const laAdmin = useAuth().hasRole('ADMIN');
  const q = useNemaTu(cabinetId);
  const nhomQ = useNhomTu(cabinetId);
  /* TK-36: lịch 48 timer của tủ — giờ cấp điện của đèn (dải lịch dim · "tắt đến 18:00").
     TK-37: đọc lịch TỦ ĐANG CHẠY (`dangChay`), KHÔNG đọc bảng `schedule` (bản đang soạn, có thể chưa đẩy xuống tủ). */
  const lichQ = useSchedule(cabinetId);
  const lichTu: NgoLich[] | null = lichQ.data?.dangChay?.outputs ?? null;
  const gui = useGuiLenhNema(cabinetId);
  const sua = useSuaDenNema(cabinetId);
  const toast = useToast();
  const [chon, setChon] = useState<number | null>(null);

  const d = q.data;
  const den = d?.den ?? [];
  const nhom = nhomQ.data ?? [];
  const ten = useMemo(() => { const m = new Map(den.map((x) => [x.addr, tenDen(x)])); return (addr: number) => m.get(addr) ?? 'Đèn'; }, [den]);
  useEffect(() => { if (chon === null && den.length) setChon(den[0].addr); }, [den, chon]);
  const sel = den.find((x) => x.addr === chon) ?? null;

  const lenh = (b: Parameters<typeof gui.mutate>[0], nhan: string, sau?: (l: LenhNema) => void) => gui.mutate(b, {
    onSuccess: (l) => { toast.success(l.state === 'CHO_GIAO_THUC' ? `${nhan} — chờ giao thức tủ` : `${nhan} — đã gửi`); sau?.(l); },
    onError: (e) => toast.error(`${nhan} — không gửi được`, errorMessage(e)),
  });

  if (q.isLoading) return <Spinner label="Đang tải đèn…" />;
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  if (!d) return null;

  const h = d.host;
  const dangQuet = h?.sweep_state === 1 || h?.sweep_state === 2;
  const giaLapTu = d.cabinet.giaLap || h?.giaLap === true;
  const soNgo = d.cabinet.numOutputs;
  /* ngõ ra đang TẮT và số đèn thuộc nhánh ấy — chỉ khi tủ online (ngoRa khác null); mất kết nối ⇒ không khai gì */
  /* TK-36: "Ngõ ra 2 tắt đến 18:00" — giờ BẬT KẾ TIẾP từ lịch tủ; giữ tay ⇒ không có giờ (lịch không điều khiển); lịch chưa biết
   *   (chưa tải / SEASON chưa có giờ mặt trời) ⇒ "đang tắt" trơn, không bịa giờ. */
  const bayGio = Date.now();
  const caiTat = (o: { i: number; tay: boolean }) => {
    if (o.tay) return 'đang tắt theo lệnh tay';
    const ngo = lichTu?.find((x) => x.index === o.i);
    const k = ngo ? batKeTiep(ngo, bayGio, d.cabinet.matTroi) : 'chua-biet';
    if (k === 'chua-biet' || (k !== null && k.dangBat)) return 'đang tắt';
    if (k === null) return 'tắt — lịch tủ không có giờ bật';
    return `tắt đến ${nhanGioBat(k, bayGio)}`;
  };
  const ngoTat = (d.ngoRa ?? []).map((o, i) => ({ i: i + 1, ...o })).filter((o) => !o.on)
    .map((o) => ({ ...o, soDen: den.filter((x) => x.nhanh === o.i).length }))
    .filter((o) => o.soDen > 0);   // TK-36: ngõ không cấp cho đèn nào ⇒ không có gì để nói (đã tự quyết)

  return (
    <div className="space-y-4" data-o="tab-den-nema">
      {/* ── 1. thanh tủ–đèn ── */}
      <section className="card flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3" data-o="thanh-tu-den">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h2 className="m-0 text-base font-semibold text-chu-1">{tenTu}</h2>
          {giaLapTu && <Badge tone="slate">GIẢ LẬP</Badge>}
          <span className="text-sm text-chu-2" data-o="tom-den">
            Đèn của tủ: <b className="tabular-nums text-chu-1">{d.tom.tong}</b>
            {/* mất kết nối ⇒ không biết ngõ ra ⇒ không khai "đang cấp điện" / "sáng" */}
            {d.ngoRa !== null && <> · đang cấp điện: <b className="tabular-nums text-chu-1">{d.tom.capDien}</b> · sáng: <b className="tabular-nums text-chu-1">{d.tom.sang}</b></>}
          </span>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2.5">
          {dangQuet && h && (
            <div className="flex items-center gap-2 text-sm text-chu-1" data-o="tien-do-quet">
              <span>Đang quét {h.sweep_done ?? 0}/{h.sweep_total ?? 0}</span>
              <div role="progressbar" aria-label="Tiến độ quét" aria-valuemin={0} aria-valuemax={100} aria-valuenow={h.sweep_total ? Math.round(((h.sweep_done ?? 0) / h.sweep_total) * 100) : 0}
                className="h-1.5 w-28 overflow-hidden rounded-full bg-nen-lom">
                <div className="h-full bg-nhan" style={{ width: `${h.sweep_total ? ((h.sweep_done ?? 0) / h.sweep_total) * 100 : 0}%` }} />
              </div>
            </div>
          )}
          <KhoaTheoTuyen qd={qd} co="canControl" hienHuyHieu={false}>
            <Button size="sm" variant="secondary" disabled={!canControl || gui.isPending} data-o="nut-nhan-den" onClick={() => lenh({ kind: 'nhan_den' }, 'Nhận đèn')}>Nhận đèn</Button>
          </KhoaTheoTuyen>
          {/* TK-44 §4: xếp đèn là việc của Cài đặt ban đầu (chỉ ADMIN; không đủ quyền thì ẩn) — không còn chế độ sửa ở /ban-do */}
          {laAdmin && <Link to={`/cai-dat-ban-dau?tu=${cabinetId}&xep=1`} className="inline-flex min-h-[36px] items-center rounded-lg border border-vien px-3 text-sm text-chu-1 hover:bg-nen-2" data-o="xep-vi-tri">Xếp vị trí đèn trên bản đồ</Link>}
          <CongTacHost cabinetId={cabinetId} hostBat={d.cabinet.hostBat} />
          {canControl && (
            <Button size="sm" variant="secondary" disabled={gui.isPending} onClick={() => lenh({ kind: dangQuet ? 'dung_quet' : 'quet_dien' }, dangQuet ? 'Dừng quét' : 'Quét')}>{dangQuet ? 'Dừng quét' : 'Quét'}</Button>
          )}
        </div>
        {ngoTat.length > 0 && (
          <p className="m-0 w-full text-xs text-chu-3" data-o="ngo-ra-tat">
            {ngoTat.map((o) => `Ngõ ra ${o.i} ${caiTat(o)} — ${o.soDen} đèn chưa cấp điện`).join(' · ')}
          </p>
        )}
      </section>

      {den.length === 0 ? (
        <EmptyState title="Chưa có đèn nào" hint="Bấm Nhận đèn để tủ ghi nhận đèn của mình." />
      ) : (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
          {/* ── 2. bảng đèn ── */}
          <section className="card overflow-hidden" data-o="bang-den">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-chu-3">
                    <th className="px-4 py-2.5 font-semibold">Đèn</th><th className="px-3 py-2.5 font-semibold">Nhánh</th><th className="px-3 py-2.5 font-semibold">Trạng thái</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Dim</th><th className="whitespace-nowrap px-3 py-2.5 text-right font-semibold">Công suất</th>
                    <th className="px-3 py-2.5 font-semibold">Vị trí</th><th className="px-3 py-2.5 font-semibold">Cập nhật</th>
                  </tr>
                </thead>
                <tbody>
                  {den.map((l) => {
                    const b = NHAN_BADGE[l.badge]; const on = l.addr === chon;
                    return (
                      <tr key={l.addr} data-den={l.addr} data-badge={l.badge} className={`border-t border-vien ${on ? 'bg-nhan/10' : 'hover:bg-nen-2/40'}`}>
                        <td className="whitespace-nowrap px-4 py-2.5"><button type="button" onClick={() => setChon(l.addr)} className="min-h-[32px] font-semibold text-chu-1 hover:text-nhan">{tenDen(l)}</button></td>
                        <td className="whitespace-nowrap px-3 py-2.5" data-o="o-nhanh">
                          {/* khoá-không-ẩn: không có quyền ⇒ ô chọn mờ + ổ khoá (CoQuyen mặc định), vẫn thấy nhánh hiện tại */}
                          <CoQuyen khoa="quan_ly_tu" hienHuyHieu={false}>
                            <select value={l.nhanh ?? ''} aria-label={`Nhánh của ${tenDen(l)}`} disabled={sua.isPending}
                              onChange={(e) => sua.mutate({ addr: l.addr, nhanh: e.target.value === '' ? null : Number(e.target.value) }, { onError: (er) => toast.error('Không lưu được nhánh', errorMessage(er)) })}
                              className="min-h-[32px] rounded-md border border-vien bg-nen-lom px-2 text-sm text-chu-1">
                              <option value="">—</option>
                              {Array.from({ length: soNgo }, (_, i) => i + 1).map((k) => <option key={k} value={k}>Ngõ {k}</option>)}
                            </select>
                          </CoQuyen>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5"><span className="flex flex-wrap gap-1"><Badge tone={b.tone}>{b.nhan}</Badge>{l.giaLap && !giaLapTu && <Badge tone="slate">GIẢ LẬP</Badge>}</span></td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">{so(l.dim, '%')}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right font-semibold tabular-nums">{so(l.p_w, 'W')}</td>
                        <td className="whitespace-nowrap px-3 py-2.5" data-o="o-vi-tri">{l.pos ? <span className="text-chu-2">Đã xếp</span> : <span className="text-chu-3">Chưa xếp</span>}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-chu-3">{tuoiNgan(l.sensor_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── đèn đang chọn ── */}
          {sel && <KhungDen cabinetId={cabinetId} den={sel} qd={qd} canControl={canControl} onLenh={lenh} dangGui={gui.isPending} />}
        </div>
      )}

      {/* ── 3. nhóm dim theo tủ ── */}
      <NhomDim cabinetId={cabinetId} den={den} nhom={nhom} qd={qd} onLenh={lenh} dangGui={gui.isPending} lenh={d.lenh} lyDoTraVe={d.lyDoTraVe} />

      {/* ── 4. lịch dim theo tủ ── */}
      <p className="m-0 text-sm text-chu-2">Lịch dim: <Link to={`/cai-dat-chien-luoc?tab=do-sang&tu=${cabinetId}`} className="font-semibold text-nhan hover:underline">Cài đặt chiến lược · Độ sáng đèn</Link></p>

      {/* ── 5. vấn đề + lệnh gần đây ── */}
      <div className="grid gap-4 xl:grid-cols-2">
        <HopVanDe pham={cabinetId} tenDen={ten} vanDeGiaLap={d.vanDeGiaLap} nguonMacDinh="nema" compact />
        <section className="card overflow-hidden" data-o="lenh-gan-day">
          <div className="flex items-center px-4 py-3"><h3 className="m-0 text-base font-semibold text-chu-1">Lệnh gần đây</h3></div>
          {d.lenh.length === 0 ? <p className="m-0 border-t border-vien px-4 py-6 text-center text-sm text-chu-3">Chưa có</p> : (
            <table className="w-full text-sm">
              <tbody>
                {d.lenh.slice(0, 6).map((l) => { const k = ketQuaLenh(l); return (
                  <tr key={l.id} className="border-t border-vien" data-lenh={l.id} data-state={l.state}>
                    <td className="px-4 py-2.5 text-chu-3 tabular-nums">{tuoiNgan(l.issuedAt)}</td>
                    <td className="px-3 py-2.5 text-chu-1">{cauLenh(l, ten)}</td>
                    <td className="px-3 py-2.5 text-right"><Badge tone={k.tone}>{k.nhan}</Badge></td>
                  </tr>
                ); })}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {/* ── 6. chi tiết kỹ thuật mạng đèn — thu gọn; CHỈ ở đây có kênh / PAN id (luật: không mã kỹ thuật trên bảng) ── */}
      {h && (
        <details className="card px-4 py-2 text-sm" data-o="ky-thuat-nema">
          <summary className="cursor-pointer text-chu-2">Chi tiết kỹ thuật mạng đèn</summary>
          <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-1 pt-2 text-chu-2 sm:grid-cols-4">
            <dt>Kênh hoạt động đã nhận</dt><dd className="m-0 tabular-nums text-chu-1">{h.rf_channel ?? '—'}</dd>
            <dt>PAN id</dt><dd className="m-0 tabular-nums text-chu-1">{h.pan_id == null ? '—' : `0x${h.pan_id.toString(16).toUpperCase().padStart(4, '0')}`}</dd>
            <dt>Số node khai</dt><dd className="m-0 tabular-nums text-chu-1">{h.so_node_khai ?? '—'}</dd>
            <dt>Nhận lúc</dt><dd className="m-0 tabular-nums text-chu-1">{h.nhan_luc ? new Date(h.nhan_luc).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : 'Chưa nhận'}</dd>
          </dl>
        </details>
      )}
    </div>
  );
}

/* ───────────────────────── nhóm dim theo tủ + dim 3 cấp (từ trang /nhom-den cũ, nay theo tủ) ───────────────────────── */

function NhomDim({ cabinetId, den, nhom, qd, onLenh, dangGui, lenh, lyDoTraVe }: {
  cabinetId: string; den: DenNema[]; nhom: ReturnType<typeof useNhomTu>['data'] extends infer T ? NonNullable<T> : never; qd: Qd; dangGui: boolean; lenh: LenhNema[];
  /** TK-44: tủ nhận "Về lịch" được không (null = được) */
  lyDoTraVe: LyDoKhongNhan | null;
  onLenh: (b: { kind: 'dim' | 'tra_ve_lich'; addrs?: number[]; level?: number; groupId?: string }, nhan: string, sau?: (l: LenhNema) => void) => void;
}) {
  const { coQuyen } = useAuth();
  const suaNhom = useSuaNhomTu(cabinetId);
  const toast = useToast();
  const dieuKhien = qd === 'cho-qua';
  const suaDuoc = coQuyen('quan_ly_tu');
  const [cap, setCap] = useState<'tu' | 'nhom' | 'den'>('nhom');
  const [nhomChon, setNhomChon] = useState<string | null>(null);
  const [denChon, setDenChon] = useState<number | null>(null);
  const [muc, setMuc] = useState(100);
  const [ketQua, setKetQua] = useState<LenhNema | null>(null);
  useEffect(() => { if (nhomChon === null && nhom.length) setNhomChon(nhom[0].id); }, [nhom, nhomChon]);
  const nhomMap = useMemo(() => new Map(nhom.map((g) => [g.id, g])), [nhom]);
  const [tick, setTick] = useState<Set<number> | null>(null);
  /* TK-39 §4: đèn mà số nhóm web ≠ số card đã xác nhận ⇒ chấm vàng + Gửi lại */
  const [guiLaiNhom, setGuiLaiNhom] = useState(false);
  const soChuaXuong = den.filter((l) => chuaXuongDen((l.groupId ? nhomMap.get(l.groupId)?.so : 0) ?? 0, l.nhomThietBi)).length;
  const tickHienTai = tick ?? new Set(den.filter((d) => d.groupId === nhomChon).map((d) => d.addr));
  useEffect(() => { setTick(null); }, [nhomChon]);
  /* kết quả hiện theo lệnh THẬT trong danh sách (xác nhận bằng quét, trạng thái tủ) — không tự khai */
  const kq = ketQua ? lenh.find((l) => l.id === ketQua.id) ?? ketQua : null;

  /* TK-44 §2: đèn ĐÍCH của cấp đang chọn ⇒ có đèn nào nhận lệnh không (máy chủ tính `nhanLenh`); không ⇒ khoá + chữ lý do */
  const dich = cap === 'tu' ? den : cap === 'nhom' ? den.filter((d) => d.groupId === nhomChon) : den.filter((d) => d.addr === denChon);
  const n = demNhanLenh(dich);
  const khoaDim = dich.length === 0 ? null : n.nhan === 0 ? chuLyDo(n.lyDo) ?? 'Không đèn nào nhận lệnh' : null;
  const khoaVe = chuLyDo(lyDoTraVe);
  const gui = (kind: 'dim' | 'tra_ve_lich') => {
    const level = kind === 'dim' ? muc : undefined;
    if (cap === 'tu') return onLenh({ kind, level }, kind === 'dim' ? `Dim cả tủ ${muc} %` : 'Về lịch cả tủ', setKetQua);
    if (cap === 'nhom' && nhomChon) {
      const g = nhomMap.get(nhomChon); const addrs = den.filter((d) => d.groupId === nhomChon).map((d) => d.addr);
      if (addrs.length === 0) return toast.error('Nhóm chưa có đèn');
      return onLenh({ kind, addrs, level, groupId: nhomChon }, kind === 'dim' ? `Dim ${g ? tenNhomSo(g) : 'nhóm'} ${muc} %` : `Về lịch ${g ? tenNhomSo(g) : 'nhóm'}`, setKetQua);
    }
    if (cap === 'den' && denChon !== null) return onLenh({ kind, addrs: [denChon], level }, kind === 'dim' ? `Dim đèn ${muc} %` : 'Về lịch đèn', setKetQua);
  };

  return (
    <section className="card overflow-hidden" data-o="nhom-dim">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3" data-o="dim-3-cap">
        <h3 className="m-0 text-base font-semibold text-chu-1">Nhóm dim</h3>
        <div className="nhom-lom inline-flex overflow-hidden rounded-lg border border-vien" role="group" aria-label="Dim cho">
          {([['tu', 'Cả tủ'], ['nhom', 'Nhóm'], ['den', 'Đèn']] as const).map(([k, t]) => (
            <button key={k} type="button" aria-pressed={cap === k} onClick={() => { setCap(k); setKetQua(null); }} className={`min-h-[36px] px-3 text-sm font-semibold ${cap === k ? 'bg-nhan text-tren-nhan' : 'bg-nen-lom text-chu-2 hover:text-chu-1'}`}>{t}</button>
          ))}
        </div>
        {cap === 'nhom' && <b className="text-sm text-chu-1">{nhomChon && nhomMap.get(nhomChon) ? tenNhomSo(nhomMap.get(nhomChon)!) : '—'}</b>}
        {cap === 'den' && (
          <select aria-label="Đèn" value={denChon ?? ''} onChange={(e) => setDenChon(e.target.value === '' ? null : Number(e.target.value))} className="min-h-[36px] rounded-lg border border-vien bg-nen-lom px-2.5 text-sm text-chu-1">
            <option value="">— chọn đèn —</option>
            {den.map((d) => <option key={d.addr} value={d.addr}>{tenDen(d)}</option>)}
          </select>
        )}
        <label htmlFor="nd-muc" className="sr-only">Mức</label>
        <input id="nd-muc" type="range" min={0} max={100} step={10} value={muc} onChange={(e) => setMuc(Number(e.target.value))} className="w-36 accent-nhan" />
        <b className="min-w-[44px] text-sm tabular-nums text-chu-1">{muc} %</b>
        <KhoaTheoTuyen qd={qd} co="canControl" hienHuyHieu={false}>
          <span className="flex gap-2">
            <Button variant="primary" size="sm" disabled={!dieuKhien || dangGui || !!khoaDim} onClick={() => gui('dim')}>Gửi</Button>
            <Button variant="secondary" size="sm" disabled={!dieuKhien || dangGui || !!khoaVe} onClick={() => gui('tra_ve_lich')}>Về lịch</Button>
          </span>
        </KhoaTheoTuyen>
        {(khoaDim ?? khoaVe) && <span className="text-xs text-chu-3" data-o="dim-khoa-vi">{khoaDim ?? khoaVe}</span>}
        {!khoaDim && dich.length > 1 && n.nhan < n.tong && <span className="text-xs text-chu-2" data-o="so-nhan-lenh">{n.nhan} / {n.tong} đèn nhận lệnh</span>}
        {kq && (() => { const k = ketQuaLenh(kq); return <Badge tone={k.tone}>{cauLenh(kq, (a) => tenDen(den.find((x) => x.addr === a) ?? { name: null, stt: null }))} · {k.nhan}</Badge>; })()}
      </div>

      {/* 390 px: `grid-cols-1` = minmax(0,1fr) — thiếu nó, cột ngầm `auto` nong theo bảng thành viên ⇒ trang tràn ngang. */}
      <div className="grid grid-cols-1 gap-0 border-t border-vien lg:grid-cols-[260px_minmax(0,1fr)]">
        {/* ── nhóm ── */}
        <div className="flex flex-col gap-2 p-3.5 lg:border-r lg:border-vien" data-o="ds-nhom">
          {nhom.map((g) => (
            <button key={g.id} type="button" aria-pressed={g.id === nhomChon} onClick={() => { setNhomChon(g.id); setCap('nhom'); }}
              className={`flex min-h-[40px] w-full items-center gap-2.5 rounded-lg border px-3 text-left text-sm font-semibold text-chu-1 ${g.id === nhomChon ? 'border-nhan bg-nhan/10' : 'border-vien bg-nen-lom hover:border-vien-manh'}`}>
              <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: g.color ?? '#64748b' }} />
              <span className="flex-1 truncate">{tenNhomSo(g)}</span>
              <span className="font-normal text-chu-3">{g.soDen}</span>
            </button>
          ))}
          {nhom.length === 0 && <p className="m-0 py-2 text-center text-sm text-chu-3">Chưa có nhóm</p>}
          <CoQuyen khoa="quan_ly_tu" an>
            <form className="mt-1 flex gap-2" onSubmit={(e) => { e.preventDefault(); const i = e.currentTarget.elements.namedItem('ten') as HTMLInputElement; const t = i.value.trim(); if (t) { suaNhom.tao.mutate({ name: t, color: MAU_NHOM[nhom.length % MAU_NHOM.length] }, { onSuccess: (g) => setNhomChon(g.id), onError: (er) => toast.error('Không tạo được nhóm', errorMessage(er)) }); i.value = ''; } }}>
              <input name="ten" placeholder="Nhóm mới" maxLength={40} aria-label="Tên nhóm mới" className="min-h-[36px] min-w-0 flex-1 rounded-lg border border-vien bg-nen-lom px-2.5 text-sm text-chu-1" />
              <Button type="submit" size="sm" variant="secondary" disabled={suaNhom.tao.isPending}>+</Button>
            </form>
          </CoQuyen>
        </div>

        {/* ── thành viên (chỉ đèn của tủ này) ── */}
        <div className="overflow-hidden" data-o="thanh-vien">
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
            <h4 className="m-0 text-sm font-semibold text-chu-1">{nhomChon ? (nhomMap.get(nhomChon) ? tenNhomSo(nhomMap.get(nhomChon)!) : '') : 'Đèn'}</h4>
            {soChuaXuong > 0 && (
              <CoQuyen khoa="quan_ly_tu" an>
                <Button size="sm" variant="secondary" disabled={guiLaiNhom} onClick={() => { setGuiLaiNhom(true); api.post<{ soLenh: number }>(`/api/nema/tu/${cabinetId}/gui-lai-nhom`).then(() => toast.success('Đã gửi lại nhóm xuống đèn')).catch((er) => toast.error('Không gửi lại được', errorMessage(er))).finally(() => setGuiLaiNhom(false)); }}>
                  Gửi lại ({soChuaXuong})
                </Button>
              </CoQuyen>
            )}
            {nhomChon && (
              <CoQuyen khoa="quan_ly_tu" an><div className="ml-auto flex gap-2">
                <Button size="sm" variant="ghost" disabled={suaNhom.xoa.isPending} onClick={() => { if (window.confirm('Xoá nhóm này? Đèn trong nhóm sẽ không còn nhóm.')) suaNhom.xoa.mutate(nhomChon, { onSuccess: (xong) => { if (!xong) return; setNhomChon(null); toast.success('Đã xoá nhóm'); }, onError: (er) => toast.error('Không xoá được', errorMessage(er)) }); }}>Xoá nhóm</Button>
                <Button size="sm" variant="primary" disabled={tick === null || suaNhom.thanhVien.isPending} onClick={() => suaNhom.thanhVien.mutate({ nhomId: nhomChon, addrs: [...tickHienTai] }, { onSuccess: () => { setTick(null); toast.success('Đã lưu'); }, onError: (er) => toast.error('Không lưu được', errorMessage(er)) })}>Lưu</Button>
              </div></CoQuyen>
            )}
          </div>
          {den.length === 0 ? <p className="m-0 border-t border-vien px-4 py-5 text-center text-sm text-chu-3">Tủ chưa có đèn</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-chu-3"><th className="px-4 py-2 font-semibold"><span className="sr-only">Chọn</span></th><th className="px-3 py-2 font-semibold">Đèn</th><th className="px-3 py-2 font-semibold">Nhánh</th><th className="px-3 py-2 font-semibold">Trạng thái</th><th className="px-3 py-2 text-right font-semibold">Dim</th><th className="px-3 py-2 font-semibold">Nhóm</th></tr></thead>
                <tbody>
                  {den.map((l) => { const trong = tickHienTai.has(l.addr); const g = l.groupId ? nhomMap.get(l.groupId) : null; const b = NHAN_BADGE[l.badge]; return (
                    <tr key={l.addr} className="border-t border-vien" data-tv={l.addr}>
                      <td className="px-4 py-2">{suaDuoc && nhomChon ? <input type="checkbox" checked={trong} aria-label={tenDen(l)} onChange={(e) => { const s = new Set(tickHienTai); if (e.target.checked) s.add(l.addr); else s.delete(l.addr); setTick(s); }} className="h-[18px] w-[18px] accent-nhan" /> : null}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-semibold text-chu-1">{tenDen(l)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-chu-2">{l.nhanh === null ? '—' : `Ngõ ${l.nhanh}`}</td>
                      <td className="whitespace-nowrap px-3 py-2"><Badge tone={b.tone}>{b.nhan}</Badge></td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{so(l.dim, '%')}</td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {g ? <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: g.color ?? '#64748b' }} />{tenNhomSo(g)}</span> : <span className="text-chu-3">—</span>}
                        {chuaXuongDen(g?.so ?? 0, l.nhomThietBi) && <span className="ml-2 inline-flex items-center gap-1 text-xs text-canh" data-chua-xuong={l.addr}><span className="h-2 w-2 rounded-full bg-canh" />chưa xuống đèn</span>}
                      </td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── đèn đang chọn ───────────────────────── */

function KhungDen({ cabinetId, den, qd, canControl, onLenh, dangGui }: {
  cabinetId: string; den: DenNema; qd: Qd; canControl: boolean; dangGui: boolean;
  onLenh: (b: { kind: 'dim' | 'quet_dien'; addrs?: number[]; level?: number }, nhan: string) => void;
}) {
  const h24 = useNema24h(cabinetId, den.addr);
  const idMien = useIdMien();   // KA-49 §3
  const sua = useSuaDenNema(cabinetId);
  const toast = useToast();
  const b = NHAN_BADGE[den.badge];
  const [dim, setDim] = useState(den.dim ?? 100);
  useEffect(() => { setDim(den.dim ?? 100); }, [den.addr, den.dim]);
  const moi = !den.name;
  const [tenMoi, setTenMoi] = useState('');
  const [congSuat, setCongSuat] = useState<120 | 200>(120);
  const coSo = den.sensor_at !== null && den.p_w !== null;
  /* TK-44 §2: lý do đèn không nhận lệnh (máy chủ tính) — Dim / Nháy khoá + chữ ngắn */
  const khoa = khoaLenhDen(den).dim;
  const rows = (h24.data?.rows ?? []).map((r) => ({ t: new Date(r.ts).getTime(), p: r.p_w }));

  return (
    <section className="card flex flex-col gap-3.5 p-4" data-o="khung-den" data-den={den.addr}>
      <div className="flex items-center gap-2.5">
        <h3 className="m-0 flex-1 truncate text-lg font-semibold text-chu-1">
          {tenDen(den)}
          {chuaDatTen(den) && <span className="ml-2 align-middle text-xs font-normal text-chu-3" data-o="chua-dat-ten">chưa đặt tên</span>}
        </h3>
        <Badge tone={b.tone}>{b.nhan}</Badge>
      </div>
      <p className="m-0 text-sm text-chu-2">{den.nhanh === null ? 'Nhánh —' : `Nhánh: Ngõ ${den.nhanh}`} · {den.pos ? 'Đã xếp trên bản đồ' : 'Chưa xếp trên bản đồ'}</p>

      {moi && (
        <form className="grid grid-cols-2 items-end gap-2.5" onSubmit={(e) => {
          e.preventDefault();
          sua.mutate({ addr: den.addr, name: tenMoi.trim() || undefined, rated_w: congSuat }, {
            onSuccess: () => toast.success('Đã lưu'), onError: (er) => toast.error('Không lưu được', errorMessage(er)),
          });
        }}>
          <label className="flex flex-col gap-1 text-xs text-chu-2">Tên
            <input value={tenMoi} onChange={(e) => setTenMoi(e.target.value)} placeholder="Trụ 07" maxLength={60} className="min-h-[40px] rounded-lg border border-vien bg-nen-lom px-2.5 text-sm text-chu-1" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-chu-2">Công suất
            <select value={congSuat} onChange={(e) => setCongSuat(Number(e.target.value) as 120 | 200)} className="min-h-[40px] rounded-lg border border-vien bg-nen-lom px-2.5 text-sm text-chu-1">
              <option value={120}>120 W</option><option value={200}>200 W</option>
            </select>
          </label>
          <Button type="submit" variant="primary" size="sm" disabled={sua.isPending || !tenMoi.trim()}>Lưu</Button>
        </form>
      )}

      {coSo && (
        <div className="grid grid-cols-3 gap-2">
          <O nhan="Công suất" gia={so(den.p_w, 'W')} />
          <O nhan="Điện áp" gia={so(den.u_v, 'V')} />
          {/* TK-38 việc 4 (lee 23/10): I hiển thị mA = i_10ma × 10 (gói card giữ 0,01 A) */}
          <O nhan="Dòng" gia={so(iMa(den.i_10ma), 'mA')} />
        </div>
      )}

      <div className="rounded-lg bg-nen-lom p-2" data-o="do-thi-24h">
        {rows.length >= 2 ? (
          <div className="h-28">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={rows} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                {defsMien(idMien, 1)}
                <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} tickFormatter={(v) => new Date(v).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={40} />
                <YAxis hide domain={[0, 'dataMax']} />
                <Tooltip labelFormatter={(v) => new Date(Number(v)).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} formatter={(v) => [`${v} W`, 'Công suất']} {...KIEU_GOI_Y} />
                <Area type="monotone" dataKey="p" stroke="rgb(var(--nhan))" fill={toMien(idMien, 0)} fillOpacity={1} strokeWidth={2} connectNulls={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : <p className="m-0 py-10 text-center text-sm text-chu-3">— chưa đủ số đo 24 giờ —</p>}
      </div>

      {/* TK-37: Nháy — khoá-không-ẩn (ngoài khối `canControl`), qua `<KhoaTheoTuyen>` trong `NutNhay` */}
      <NutNhay cabinetId={cabinetId} addr={den.addr} ten={tenDen(den)} khoaVi={khoa} qd={qd} />
      {canControl && (
        <div className="flex flex-wrap items-center gap-2.5">
          <label htmlFor={`nema-dim-${den.addr}`} className="text-sm text-chu-2">Dim</label>
          <input id={`nema-dim-${den.addr}`} type="range" min={0} max={100} step={10} value={dim} onChange={(e) => setDim(Number(e.target.value))} className="w-32 accent-nhan" />
          <b className="min-w-[44px] text-sm tabular-nums text-chu-1">{dim} %</b>
          <Button size="sm" variant="primary" disabled={dangGui || !!khoa} onClick={() => onLenh({ kind: 'dim', addrs: [den.addr], level: dim }, `Dim ${tenDen(den)} ${dim} %`)}>Gửi</Button>
          {khoa && <span className="text-xs text-chu-3" data-o="dim-khoa-vi">{khoa}</span>}
          <Link to={`/ban-do?tu=${cabinetId}`} className="inline-flex min-h-[36px] items-center rounded-lg border border-vien px-3 text-sm text-chu-1 hover:bg-nen-2">Bản đồ</Link>
        </div>
      )}
    </section>
  );
}

function O({ nhan, gia }: { nhan: string; gia: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg bg-nen-lom px-3 py-2.5">
      <span className="text-xs text-chu-3">{nhan}</span>
      <b className="text-lg font-semibold tabular-nums text-chu-1">{gia}</b>
    </div>
  );
}

/* NHAN_TRANG_THAI_LENH giữ cho chỗ nhập cũ (ketQuaLenh đã dùng bên trong nemaApi). */
void NHAN_TRANG_THAI_LENH;
export type { TuNema };
